import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { resolveTenantSlugGuard } from "@lms/backend/lib/auth/tenant-slug-middleware";
import { useAuthStore } from "@/stores/authStore";
import { recentIdleSignOut, signInPath } from "@/lib/session-activity";
import { isSessionExpiring } from "@/services/http/session-refresh";

/** Pages that need a session; a signed-out visitor goes to login and comes back (AUTH-11). */
const PROTECTED_PREFIXES = [
  "/dashboard",
  "/instructor",
  "/learn",
  "/forum",
  "/messages",
  "/notifications",
  "/admin",
  "/settings",
  "/subscriptions",
  "/checkout",
];

function isProtectedTenantPath(pathname: string, tenantSlug: string): boolean {
  const base = `/t/${tenantSlug}`;
  if (!pathname.startsWith(base)) return false;
  const rest = pathname.slice(base.length);
  return PROTECTED_PREFIXES.some((segment) => rest === segment || rest.startsWith(`${segment}/`));
}

export function TenantSlugGuard({
  tenantSlug,
  children,
}: {
  tenantSlug: string;
  children: React.ReactNode;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  useEffect(() => {
    const guard = resolveTenantSlugGuard({
      pathname: location.pathname,
      sessionTenantSlug: user?.tenantSlug,
      isAuthenticated,
      role: user?.role,
    });

    // MT-1 S24: the shared guard leaves the sign-in page and the one-time links (SPA-2) alone and
    // sends any other page of another school to that school's sign-in page.
    if (guard.action === "redirect") {
      navigate(guard.redirectPath, { replace: true });
      return;
    }

    // An expired session is already on its way to `/login?reason=expired&next=` (C2).
    if (
      isProtectedTenantPath(location.pathname, tenantSlug) &&
      !isAuthenticated &&
      !isSessionExpiring()
    ) {
      // SPA-1: the session store re-renders before the idle sign-out's own navigation commits,
      // so this redirect lands last; keep its "signed out for inactivity" reason.
      navigate(
        signInPath(
          tenantSlug,
          `${location.pathname}${location.search}`,
          recentIdleSignOut() ? "idle" : undefined,
        ),
        { replace: true },
      );
    }
  }, [location.pathname, location.search, tenantSlug, user, isAuthenticated, navigate]);

  return <>{children}</>;
}

export function AuthRequiredLink({
  tenantSlug,
  signedIn,
  className,
  children,
  ...props
}: {
  tenantSlug: string;
  signedIn: boolean;
  className?: string;
  children: React.ReactNode;
  "data-testid"?: string;
}) {
  const href = signedIn ? `/t/${tenantSlug}/dashboard` : `/t/${tenantSlug}/login`;
  const testId = props["data-testid"];
  return (
    <Link to={href} className={className} data-testid={testId}>
      {children}
    </Link>
  );
}
